/**
 * LEGACY: the old Vuex store, kept until the app has moved off it; don't use it in new code.
 * The new global state is appState (src/appState.ts): a plain reactive object, no Vuex, no Pinia.
 * Boy-scout rule: when you touch code that reads or writes this store, move that piece to appState.
 * Careful: store.getters.isLoggedIn survives a page reload (vuex-persist), while appState.isLoggedIn is false until the session is read again (the router guard's hydrate), so it is no drop-in replacement yet.
 * Keep the persist key 'bcc-dashboard': changing it logs every user out.
 */
import { createStore } from 'vuex'
import {
  activeLayout,
  defaultPage,
  isLoggedIn,
  updateSession,
  logout,
  setActiveSite,
  getSearchQuery,
  setSearchQuery,
  registerUserActivity,
  updateSessionTimeout,
  clearSessionTimeout,
  hasPermissions,
  getInstitutionDeathClaims,
  setInstitutionDeathClaims,
  isProfileUpToDate,
  setIsProfileUpToDate,
} from './vuex-actions'
import { STAFF, MANAGER, OSR, CLIENT, TEAM } from './roles'
import { CLIENT_LAYOUT, OSR_LAYOUT, ADMIN, DEFAULT_CLIENT_PAGE, DEFAULT_ADMIN_PAGE } from './layouts'
import Axios from './axios'
import VuexPersist from 'vuex-persist'
import { confirmRefreshSession } from './components/dialogs'
import { useGlobalUtils } from './helpers/globalUtils'
import moment from 'moment-timezone'
import { LOGIN_ROUTE_NAME } from './components/auth/authConfig'
import { authFlow } from './lib/auth'
import { appState } from './appState'

const { errorToast } = useGlobalUtils()

const vuexPersist = new VuexPersist({
  key: 'bcc-dashboard',
  storage: localStorage,
})

// The session's length is the login cookie's sliding window (clientSession.msLength, from the server's sessionTimeoutSeconds); these are UI choices within it
let msBuffer = 1 * 60 * 1000 // the expiry prompt's countdown
const WARNING_BUFFER = 1 * 60 * 1000 // 1 minute warning
const ACTIVITY_CHECK_INTERVAL = 60 * 1000 // Check every minute

/**
 * The inactivity before the expiry prompt: half the cookie's window.
 * The cookie handler issues the cookie again only once half its window has passed, so after the last refresh it surely lives this long, enough for the prompt's countdown.
 */
const inactivityThreshold = (msLength) => msLength / 2

/**
 * Ends the login here and opens the login screen at once: no page reload, no waiting for the server.
 * The server's logout runs in the background (authFlow.logout), and the login screen fills itself from the last login.
 */
export async function logOut() {
  endLogin()
  // router.js imports this file, so the router is read here, at call time, not at module load
  const { default: router } = await import('./router')
  await router.replace({ name: LOGIN_ROUTE_NAME })
}

/** The local part of the logout, for the router guard, which redirects by itself: the flow, appState and Vuex cleared */
export function endLogin() {
  // empties appState's session, and the glue (authSetup.ts) clears Vuex with it
  authFlow.logout()
  // the glue clears Vuex only when appState had a session, which after a page reload it may never have had
  store.commit(logout)
  // the reload used to reset it; a logout started from a busy screen leaves it set
  store.state.isLoading = false
}

async function launchSessionExpiredPrompt() {
  if (localStorage.getItem('annualQuestionnaireIsOpen') === 'true') {
    console.info('Annual Questionnaire is open, skipping session prompt...')
    store.state.attemptedSessionRefresh += 1
    return
  }

  if (!store.getters.isLoggedIn) {
    return
  }

  // Disable select portions of the screen
  let menuControl = document.querySelector('.menu-control-top')
  let collapseControl = document.querySelector('.menu-control-bottom')

  if (menuControl) {
    menuControl.style.display = 'none'
  }

  if (collapseControl) {
    collapseControl.style.display = 'none'
  }

  let secondsLeft = Math.round(msBuffer / 1000)

  const onConfirm = async function () {
    store.state.isLoading = true

    try {
      const req = await Axios.get(`/session/refresh`)

      if (req && req.status !== 200) {
        throw new Error('Failed to refresh session')
      }
    } catch (e) {
      errorToast('Failed to refresh session, logging out.')
      await logOut()
    } finally {
      if (menuControl) {
        menuControl.style.display = 'flex'
      }

      if (collapseControl) {
        collapseControl.style.display = 'flex'
      }

      store.state.isLoading = false
    }
  }

  const onCancel = async function () {
    await logOut()

    if (menuControl) {
      menuControl.style.display = 'flex'
    }

    if (collapseControl) {
      collapseControl.style.display = 'flex'
    }
  }

  const userChoice = await confirmRefreshSession(secondsLeft)

  userChoice === true ? await onConfirm() : await onCancel()
}

function getMsBuffer(msLength) {
  let buf = msBuffer
  if (buf >= msLength) {
    buf = 30 * 1000
  }
  return buf
}

async function checkActivityAndRefreshSession() {
  // no window yet: the login hasn't handed Vuex its session (authSetup.ts)
  const msLength = store.state.clientSession?.msLength
  if (!store.getters.isLoggedIn || !store.state.lastUserActivity || !msLength) {
    return
  }

  const now = moment().utc()
  const lastActivity = moment(store.state.lastUserActivity)
  const inactiveTime = now.diff(lastActivity)

  // If user has been inactive for half the session, show the session expired prompt
  if (inactiveTime >= inactivityThreshold(msLength)) {
    console.info('User inactive for half the session, launching session expired prompt...')
    await launchSessionExpiredPrompt()
    return
  }

  // If user has been active within the session length, refresh session
  if (inactiveTime < msLength - WARNING_BUFFER) {
    try {
      console.info('User active, refreshing session...')
      const response = await Axios.get('/session/refresh')

      if (response && response.status !== 200) {
        throw new Error('Failed to refresh session')
      }

      store.commit(updateSessionTimeout)
    } catch (error) {
      console.error('Failed to refresh session:', error)
      await launchSessionExpiredPrompt()
    }
  }
}

/** @deprecated The legacy Vuex store: new code uses appState (src/appState.ts); see the note at the top of this file */
export const store = createStore({
  plugins: [vuexPersist.plugin],
  state: {
    isLoading: false,
    attemptedSessionRefresh: 0,
    timeoutPrompt: null,
    timeout: null,
    searchQuery: '',
    clientSession: {
      siteId: 0,
      userId: 0,
      userRole: CLIENT,
      isMFAEnabled: false,
      isMFAValid: false,
      institutionId: 0,
      // set by the login (authSetup.ts toClientSession)
      msLength: 0,
    },
    activeSite: {
      id: 0,
      name: '',
      displayName: '',
      url: '',
      logoUrl: '',
      linkedUrl: '',
    },
    lastUserActivity: null,
    activityCheckInterval: null,
    institutionDeathClaims: [],
    hasNewAccountRequests: false,
  },
  getters: {
    [hasPermissions]: ({ state }, permissions = []) => {
      return state.clientSession && state.clientSession.permissions.filter((permission) => permissions.includes(permission)).length > 1
    },
    [getSearchQuery]: (state) => {
      return state.searchQuery
    },
    [getInstitutionDeathClaims]: (state) => {
      return state.institutionDeathClaims
    },
    [activeLayout]: (state) => {
      if (!state.clientSession) {
        return CLIENT_LAYOUT
      }

      if (state.clientSession && [STAFF, MANAGER].includes(state.clientSession.userRole)) {
        return ADMIN
      }
      if (state.clientSession.userRole === OSR || state.clientSession.userRole === TEAM) {
        return OSR_LAYOUT
      }
      if (state.clientSession.userRole === CLIENT) {
        return CLIENT_LAYOUT
      }
      return CLIENT_LAYOUT // default
    },
    [isLoggedIn]: (state) => {
      return state.clientSession && state.clientSession.userId > 0
    },
    [defaultPage]: (state) => {
      if (state.clientSession && [STAFF, MANAGER].includes(state.clientSession.userRole)) {
        return DEFAULT_ADMIN_PAGE
      }
      return DEFAULT_CLIENT_PAGE // default
    },
    [isProfileUpToDate]: (state) => {
      return state.clientSession && state.clientSession.isProfileUpToDate
    },
  },
  mutations: {
    [setIsProfileUpToDate](state, isProfileUpToDate) {
      if (state.clientSession) {
        state.clientSession.isProfileUpToDate = isProfileUpToDate
      }
    },
    [setSearchQuery](state, searchQuery) {
      state.searchQuery = searchQuery
    },
    [setInstitutionDeathClaims](state, deathClaims) {
      state.institutionDeathClaims = deathClaims
    },
    [updateSessionTimeout](state) {
      console.info('Updating session timeout...')

      if (!state.clientSession) {
        return
      }

      // Clear existing timeouts
      if (state.timeoutPrompt) {
        clearTimeout(state.timeoutPrompt)
      }
      if (state.timeout) {
        clearTimeout(state.timeout)
      }
      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
      }

      // Start activity checking
      state.activityCheckInterval = setInterval(checkActivityAndRefreshSession, ACTIVITY_CHECK_INTERVAL)
    },
    [clearSessionTimeout](state) {
      clearTimeout(state.timeoutPrompt)
      state.timeoutPrompt = null
      clearTimeout(state.timeout)
      state.timeout = null

      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
        state.activityCheckInterval = null
      }
    },
    [updateSession](state, session) {
      console.info('Updating session...')

      if (state.timeoutPrompt && state.timeoutPrompt !== null) {
        clearTimeout(state.timeoutPrompt)
      }
      if (state.timeout && state.timeout !== null) {
        clearTimeout(state.timeout)
      }

      if (state.clientSession) {
        state.clientSession = session
        if (state.clientSession.msLength <= msBuffer) {
          msBuffer = getMsBuffer(state.clientSession.msLength)
        }
        let timeout = state.clientSession.msLength - msBuffer
        //  state.timeoutPrompt = setTimeout(launchSessionExpiredPrompt, timeout)
        //state.timeout = setTimeout(logOut, state.clientSession.msLength)
      }
    },
    [registerUserActivity](state) {
      state.lastUserActivity = moment().utc()
      console.debug('User activity registered at:', state.lastUserActivity.format())
    },
    [logout](state) {
      // default session again
      clearTimeout(state.timeoutPrompt)
      state.timeoutPrompt = null
      clearTimeout(state.timeout)
      state.timeout = null

      if (state.clientSession) {
        state.clientSession = {
          siteId: 0,
          userId: 0,
          userRole: CLIENT,
          institutionId: 0,
          msLength: 0,
        }
      }

      // Clear activity interval
      if (state.activityCheckInterval) {
        clearInterval(state.activityCheckInterval)
        state.activityCheckInterval = null
      }
    },
    [setActiveSite](state, site) {
      state.activeSite = site
    },
  },
  actions: {
    async updateActiveSite(context) {
      try {
        let route = `/site`
        let response = await Axios.get(route)
        context.commit(setActiveSite, response.data)
      } catch (error) {
      } finally {
        // #0612: the new screens read the site from appState; after a failed call it's the copy vuex-persist kept from the last visit
        const { name, displayName, url, logoUrl, linkedUrl } = context.state.activeSite ?? {}
        appState.site = { name, displayName, url, logoUrl, linkedUrl }
      }
    },
  },
})
